import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import type { FieldMatchingSettings } from '@/api/domains/field-matching.types';
import type { FieldMatchingGridProps, FormSchema } from '@/components/FieldMatching';
import { FieldMatchingGrid, renderGridHeader, useFieldMatchingGrid } from '@/components/FieldMatching';

import { useReconciliationFile } from '../../reconciliation-file.store';
import { ReconciliationMatchingOptions } from './ReconciliationMatchingOptions';

type ReconciliationFieldMatchingDataGridProps = {
  fieldMatchingSettings?: FieldMatchingSettings;
};

export const ReconciliationFieldMatchingDataGrid = ({
  fieldMatchingSettings,
}: ReconciliationFieldMatchingDataGridProps) => {
  const { control } = useFormContext<FormSchema>();
  const startRow = Number(useWatch({ control, name: 'startRow' }));

  const { numberOfColumnsFromSettings, columns, lockedRows, dataSource, handleUpload } = useFieldMatchingGrid({
    renderGridHeader,
    fieldMatchingSettings,
  });

  const disabledRows = useMemo(() => {
    return dataSource.reduce<FieldMatchingGridProps['disabledRows']>((acc, _, index) => {
      if (!acc) {
        return null;
      }

      if (index < startRow - 1) {
        acc[index] = true;
      }
      return acc;
    }, {});
  }, [startRow, dataSource]);

  const reconciliationFile = useReconciliationFile();

  return (
    <FieldMatchingGrid
      loading={false}
      idProperty="rowNumber"
      columns={columns}
      dataSource={dataSource}
      disabledRows={disabledRows}
      lockedRows={lockedRows}
      numberOfColumnsFromSettings={numberOfColumnsFromSettings}
      onUpload={handleUpload}
      uploadedFile={reconciliationFile}
      renderGridOption={columnNumber => <ReconciliationMatchingOptions columnNumber={columnNumber} />}
    />
  );
};
